import { DataverseEntityInfo, DataverseFieldInfo } from '@shared/api-types';
import { FieldProvider } from './filter-builder-types';
import { getOperatorsForFieldType } from './operators-config';

// Base field provider that can be extended for different contexts
export abstract class BaseFieldProvider implements FieldProvider {
  protected baseUrl: string;
  
  constructor(baseUrl: string = '/api') {
    this.baseUrl = baseUrl;
  }

  abstract getFields(entityLogicalName: string): Promise<DataverseFieldInfo[]>;
  abstract getLookupFields(entityLogicalName: string): Promise<DataverseFieldInfo[]>;
  abstract getRelatedEntities(entityLogicalName: string): Promise<DataverseEntityInfo[]>;

  // Helper method to normalize field info
  protected normalizeFieldInfo(field: any): DataverseFieldInfo {
    return {
      logicalName: field.LogicalName || field.logicalName,
      displayName: field.DisplayName?.UserLocalizedLabel?.Label || field.displayName || field.LogicalName || field.logicalName,
      type: this.mapAttributeType(field.AttributeType || field.type),
      isRequired: field.IsValidForRead !== false,
      isPrimaryKey: field.IsPrimaryId || field.isPrimaryKey,
      isLookup: field.AttributeType === 'Lookup' || field.isLookup,
      MetadataId: field.MetadataId,
      AttributeType: field.AttributeType,
      IsPrimaryId: field.IsPrimaryId,
      IsValidForRead: field.IsValidForRead,
      LogicalName: field.LogicalName,
      SchemaName: field.SchemaName,
      DisplayName: field.DisplayName,
      Description: field.Description
    };
  }

  // Helper method to normalize entity info
  protected normalizeEntityInfo(entity: any): DataverseEntityInfo {
    return {
      MetadataId: entity.MetadataId,
      LogicalName: entity.LogicalName || entity.logicalName,
      SchemaName: entity.SchemaName || entity.displayName || entity.LogicalName || entity.logicalName,
      ObjectTypeCode: entity.ObjectTypeCode || entity.objectTypeCode,
      logicalName: entity.LogicalName || entity.logicalName,
      displayName: entity.SchemaName || entity.displayName || entity.LogicalName || entity.logicalName,
      objectTypeCode: entity.ObjectTypeCode || entity.objectTypeCode
    };
  }

  // Map Dataverse attribute types to simplified types
  private mapAttributeType(attributeType: string): string {
    if (!attributeType) return 'string';
    
    switch (attributeType.toLowerCase()) {
      case 'string':
      case 'memo':
        return 'string';
      case 'integer':
      case 'decimal':
      case 'double':
      case 'money':
        return 'number';
      case 'boolean':
        return 'boolean';
      case 'datetime':
        return 'datetime';
      case 'lookup':
      case 'customer':
      case 'owner':
        return 'lookup';
      case 'picklist':
      case 'state':
      case 'status':
        return 'choice';
      case 'uniqueidentifier':
        return 'guid';
      default:
        return 'string';
    }
  }

  // Get operators for a specific field type
  public getOperatorsForFieldType(fieldType: string) {
    return getOperatorsForFieldType(fieldType);
  }
}

// Field provider for Entity Mappings context
export class EntityMappingsFieldProvider extends BaseFieldProvider {
  async getFields(entityLogicalName: string): Promise<DataverseFieldInfo[]> {
    try {
      const params = new URLSearchParams({ entityName: entityLogicalName });
      const response = await fetch(`${this.baseUrl}/entity-mappings/fields?${params.toString()}`);
      if (!response.ok) throw new Error(`Failed to fetch fields: ${response.statusText}`);
      
      const data = await response.json();
      return Array.isArray(data) ? data.map(field => this.normalizeFieldInfo(field)) : [];
    } catch (error) {
      console.error('Error fetching entity fields:', error);
      return [];
    }
  }

  async getLookupFields(entityLogicalName: string): Promise<DataverseFieldInfo[]> {
    const allFields = await this.getFields(entityLogicalName);
    return allFields.filter(field => field.isLookup);
  }

  async getRelatedEntities(entityLogicalName: string): Promise<DataverseEntityInfo[]> {
    try {
      // Fall back to returning all entities for now (relationships can be derived client-side if needed).
      const response = await fetch(`${this.baseUrl}/entity-mappings/entities`);
      if (!response.ok) throw new Error(`Failed to fetch entities: ${response.statusText}`);

      const data = await response.json();
      return Array.isArray(data) ? data.map(entity => this.normalizeEntityInfo(entity)) : [];
    } catch (error) {
      console.error('Error fetching related entities:', error);
      return [];
    }
  }
}

// Field provider for Tasks context
export class TasksFieldProvider extends BaseFieldProvider {
  // Tasks might have a different API endpoint structure
  async getFields(entityLogicalName: string): Promise<DataverseFieldInfo[]> {
    // For tasks, we might need to call a different endpoint
    // This is a placeholder - adjust based on your actual API
    return this.getFieldsFromTasksAPI(entityLogicalName);
  }

  async getLookupFields(entityLogicalName: string): Promise<DataverseFieldInfo[]> {
    const allFields = await this.getFields(entityLogicalName);
    return allFields.filter(field => field.isLookup);
  }

  async getRelatedEntities(entityLogicalName: string): Promise<DataverseEntityInfo[]> {
    // Tasks might have different related entity logic
    return this.getRelatedEntitiesFromTasksAPI(entityLogicalName);
  }

  private async getFieldsFromTasksAPI(entityLogicalName: string): Promise<DataverseFieldInfo[]> {
    // Implement tasks-specific field fetching logic here
    // This might use a different API endpoint or have different data structure
    try {
      const response = await fetch(`${this.baseUrl}/tasks/entity-fields/${entityLogicalName}`);
      if (!response.ok) throw new Error(`Failed to fetch task fields: ${response.statusText}`);
      
      const data = await response.json();
      return Array.isArray(data) ? data.map(field => this.normalizeFieldInfo(field)) : [];
    } catch (error) {
      console.error('Error fetching task entity fields:', error);
      return [];
    }
  }

  private async getRelatedEntitiesFromTasksAPI(entityLogicalName: string): Promise<DataverseEntityInfo[]> {
    // Implement tasks-specific related entity fetching logic
    try {
      const response = await fetch(`${this.baseUrl}/tasks/related-entities/${entityLogicalName}`);
      if (!response.ok) throw new Error(`Failed to fetch task related entities: ${response.statusText}`);
      
      const data = await response.json();
      return Array.isArray(data) ? data.map(entity => this.normalizeEntityInfo(entity)) : [];
    } catch (error) {
      console.error('Error fetching task related entities:', error);
      return [];
    }
  }
}

// Factory function to create appropriate field provider
export function createFieldProvider(contextType: string, baseUrl?: string): FieldProvider {
  switch (contextType) {
    case 'entity-mappings':
      return new EntityMappingsFieldProvider(baseUrl);
    case 'tasks':
      return new TasksFieldProvider(baseUrl);
    default:
      return new EntityMappingsFieldProvider(baseUrl);
  }
}
